import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { PlayCircle } from '@phosphor-icons/react'

import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import NumericInput from '../common/components/formInputs/NumericInput'
import CheckboxInput from '../common/components/formInputs/CheckboxInput'
import TicketsSection from '../common/components/formInputs/TicketsSection'
import AdvancedTicketingUpselling from '../common/components/AdvancedTicketingUpselling/AdvancedTicketingUpselling'
import FormInput from '../common/components/formInputs/FormInput'
import TextField from '../../../components/FormLegacy/FormFieldsV2/TextField'

const ThirdStep = ({ displayStep, formValues, setFieldValue, taken }) => {
  const { t } = useTranslation(['eventManagement'])
  const minEventCapacityValue = taken ?? 0

  const [isUpsellingModalOpen, setIsUpsellingModalOpen] = useState(false)
  const selectedBuilding = useSelector(selectActiveBuilding)
  const advancedTicketingEnabled = !!formValues.building?.eventTicketsAvailable

  if (!displayStep) {
    return null
  }

  const { guestsAmount, capacity, collectGuestsInfo, tickets } = formValues

  const guestAmmountErrorMessage =
    guestsAmount >= capacity ? t('eventManagement:eventFormPage.thirdStep.guestsAmountError') : ''

  return (
    <React.Fragment>
      {advancedTicketingEnabled ? (
        <>
          <div className="columns ">
            <div className="column is-12">
              <div className="ticket-upgrade-banner">
                <div className="ticket-upgrade-banner__content">
                  <img src="/images/star.svg" alt="" className="ticket-upgrade-banner__icon" />
                  <div className="ticket-upgrade-banner__text">
                    <p className="ticket-upgrade-banner__title">
                      {t('eventManagement:eventFormPage.thirdStep.upgradeBannerTitle')}
                    </p>
                    <p className="ticket-upgrade-banner__description">
                      {t('eventManagement:eventFormPage.thirdStep.upgradeBannerDescription')}
                    </p>
                  </div>
                </div>
                <button
                  className="ticket-upgrade-banner__button"
                  type="button"
                  onClick={() => setIsUpsellingModalOpen(true)}
                >
                  <PlayCircle size={16} />
                  {t('eventManagement:eventFormPage.thirdStep.watchTutorial')}
                </button>
              </div>
            </div>
          </div>
          <div className="columns">
            <NumericInput
              columnSize={6}
              name="capacity"
              label={t('eventManagement:eventFormPage.thirdStep.capacityLabel')}
              value={capacity}
              setFieldValue={setFieldValue}
              onlyIntegers
              min={minEventCapacityValue}
            />
            <NumericInput
              columnSize={advancedTicketingEnabled ? 6 : 12}
              name="guestsAmount"
              label={t('eventManagement:eventFormPage.thirdStep.guestsAmountLabel')}
              value={guestsAmount}
              setFieldValue={setFieldValue}
              errorText={guestAmmountErrorMessage}
              onlyIntegers
              onlyPositive={false}
              min={0}
              max={capacity}
            />
          </div>
          <div className="columns">
            <div className="column is-6" />
            <CheckboxInput
              columnSize={6}
              name="collectGuestsInfo"
              label={t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoLabel')}
              value={collectGuestsInfo}
              setFieldValue={setFieldValue}
              tooltip={
                <>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine1')}
                  </p>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine2')}
                  </p>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine3')}
                  </p>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine4')}
                  </p>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine5')}
                  </p>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine6')}
                  </p>
                </>
              }
            />
          </div>
          <TicketsSection
            name="tickets"
            tickets={tickets}
            setFieldValue={setFieldValue}
            advancedTicketingEnabled={advancedTicketingEnabled}
          />
          {isUpsellingModalOpen && (
            <AdvancedTicketingUpselling
              onClose={() => setIsUpsellingModalOpen(false)}
              buildingId={selectedBuilding?.id}
              advancedTicketingEnabled={advancedTicketingEnabled}
            />
          )}
        </>
      ) : (
        <>
          <div className="columns">
            <NumericInput
              columnSize={6}
              name="capacity"
              label={t('eventManagement:eventFormPage.thirdStep.capacityLabel')}
              value={capacity}
              setFieldValue={setFieldValue}
              onlyIntegers
              min={minEventCapacityValue}
            />
            <div className="column is-6">
              <FormInput label={t('eventManagement:common.priceLabel')} name="price">
                <TextField
                  type="number"
                  onlyIntegers
                  onChange={(e) => {
                    const value = e.target.value
                    const maxPrice = 1000000

                    if (value.includes('.')) {
                      return
                    }

                    if (parseFloat(value) > maxPrice) {
                      setFieldValue(`tickets[0].price`, value)
                      return
                    }

                    setFieldValue(`tickets[0].price`, value)
                  }}
                  value={tickets[0].price}
                  min={0}
                />
              </FormInput>
            </div>
          </div>
          <div className="columns">
            <NumericInput
              columnSize={12}
              name="guestsAmount"
              label={t('eventManagement:eventFormPage.thirdStep.guestsAmountLabel')}
              value={guestsAmount}
              setFieldValue={setFieldValue}
              errorText={guestAmmountErrorMessage}
              onlyIntegers
              onlyPositive={false}
              min={0}
              max={capacity}
            />
          </div>
          <div className="columns">
            <CheckboxInput
              columnSize={6}
              name="collectGuestsInfo"
              label={t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoLabel')}
              value={collectGuestsInfo}
              setFieldValue={setFieldValue}
              tooltip={
                <>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine1')}
                  </p>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine2')}
                  </p>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine3')}
                  </p>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine4')}
                  </p>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine5')}
                  </p>
                  <p>
                    {t('eventManagement:eventFormPage.thirdStep.collectGuestsInfoTooltipLine6')}
                  </p>
                </>
              }
            />
          </div>
        </>
      )}
    </React.Fragment>
  )
}

ThirdStep.propTypes = {
  displayStep: PropTypes.bool,
  formValues: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  taken: PropTypes.number,
}

export default ThirdStep
